<template>
  <Card class="layout-card after-sales-page">
    <div class="page-scroll">
      <section>
        <h2 class="section-title overview-title">客诉概览</h2>
        <div class="overview-grid">
          <div
            v-for="card in overviewCards"
            :key="card.value"
            class="overview-card"
          >
            <div class="overview-card-header">
              <strong :style="{ color: card.color }">{{ card.label }}</strong>
              <strong :style="{ color: card.color }">
                {{ card.totalCount }}
              </strong>
            </div>
            <div class="overview-card-body">
              <div class="refund-summary">
                <div class="summary-name">
                  <Icon type="logo-usd" color="#ff5b35" size="20" />
                  <span>退款</span>
                </div>
                <div class="summary-values">
                  <span>
                    <em>数量</em>
                    <strong>{{ card.refundCount }}</strong>
                  </span>
                  <span>
                    <em>金额</em>
                    <strong>{{
                      formatOverviewAmount(card.refundAmount)
                    }}</strong>
                  </span>
                </div>
              </div>
              <div class="operation-summary-row">
                <div class="operation-summary">
                  <Icon type="md-document" color="#1677ff" size="18" />
                  <span>补单</span>
                  <strong>{{ card.supplementCount }}</strong>
                </div>
                <div class="operation-summary">
                  <Icon type="md-close" color="#ff4d4f" size="18" />
                  <span>取消订阅</span>
                  <strong class="cancel-count">
                    {{ card.cancelSubscriptionCount }}
                  </strong>
                </div>
              </div>
            </div>
            <Spin v-if="overviewLoading" fix />
          </div>
        </div>
      </section>

      <Divider class="section-divider" />

      <section>
        <h2 class="section-title">售后管理</h2>
        <div class="search-toolbar">
          <Input
            v-model.trim="searchForm.keyword"
            clearable
            placeholder="搜索用户ID / 订单号 / 平台订单号 / 订阅ID / 商品"
            class="keyword-input"
            @on-enter="getData()"
          >
            <Icon slot="prefix" type="md-search" />
          </Input>
          <Select
            v-model="searchForm.operationType"
            clearable
            placeholder="售后类型"
            class="type-select"
          >
            <Option
              v-for="item in operationTypeOptions"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </Option>
          </Select>
          <Button
            type="primary"
            icon="md-search"
            :loading="tableLoading"
            @click="getData()"
          >
            搜索
          </Button>
          <Button icon="md-refresh" @click="handleReset">重置</Button>
        </div>

        <div class="table-wrap">
          <Table
            border
            :columns="columns"
            :data="tableData"
            :loading="tableLoading"
            no-data-text="暂无售后记录"
          >
            <template slot="userId" slot-scope="{ row }">
              <div class="dual-user-id">
                <CopyableText :value="row.publicUserId" mono />
                <small>主键 <CopyableText :value="row.userId" mono /></small>
              </div>
            </template>
            <template slot="orderId" slot-scope="{ row }">
              <CopyableText :value="row.orderId" mono />
            </template>
            <template slot="orderNo" slot-scope="{ row }">
              <CopyableText :value="row.orderNo" mono />
            </template>
            <template slot="platformOrderNo" slot-scope="{ row }">
              <CopyableText :value="row.platformOrderNo" mono />
            </template>
            <template slot="subscriptionId" slot-scope="{ row }">
              <CopyableText :value="row.subscriptionId" mono />
            </template>
            <template slot="goodsName" slot-scope="{ row }">
              {{ displayValue(row.goodsName) }}
            </template>
            <template slot="goodsType" slot-scope="{ row }">
              {{ getGoodsTypeLabel(row.goodsType) }}
            </template>
            <template slot="payPlatform" slot-scope="{ row }">
              {{ getPayPlatformLabel(row.payPlatform) }}
            </template>
            <template slot="amount" slot-scope="{ row }">
              <strong>{{ formatTableAmount(row.amount, row.currency) }}</strong>
            </template>
            <template slot="operationAmount" slot-scope="{ row }">
              <strong
                v-if="
                  row.operationAmount !== null &&
                  row.operationAmount !== undefined
                "
              >
                {{
                  formatTableAmount(
                    row.operationAmount,
                    row.operationCurrency || row.currency
                  )
                }}
              </strong>
              <span v-else>—</span>
            </template>
            <template slot="operationType" slot-scope="{ row }">
              <Tag :color="getOperationTypeColor(row.operationType)">
                {{ getOperationTypeLabel(row.operationType) }}
              </Tag>
            </template>
            <template slot="payStatus" slot-scope="{ row }">
              <Tag :color="getPayStatusColor(row.payStatus)">
                {{ getPayStatusLabel(row.payStatus) }}
              </Tag>
            </template>
            <template slot="operationStatus" slot-scope="{ row }">
              <Tag :color="getOperationStatusColor(row.operationStatus)">
                {{ getOperationStatusLabel(row.operationStatus) }}
              </Tag>
            </template>
            <template slot="status" slot-scope="{ row }">
              <span v-if="row.goodsType === 0" class="empty-status">—</span>
              <Tag
                v-else
                :color="getSubscriptionStatusColor(row.subscriptionStatus)"
              >
                {{ getSubscriptionStatusLabel(row.subscriptionStatus) }}
              </Tag>
            </template>
            <template slot="operateTime" slot-scope="{ row }">
              {{ formatDateTime(row.operateTime) }}
            </template>
            <template slot="operator" slot-scope="{ row }">
              {{ row.operatorName || '管理员' }}
            </template>
            <template slot="action" slot-scope="{ row }">
              <Button size="small" icon="md-eye" @click="handleOpenDetail(row)">
                详情
              </Button>
            </template>
          </Table>
          <Pages
            v-if="page.total"
            class="list-pagination"
            :page="page"
            :func="getData"
          />
        </div>
      </section>
    </div>

    <Modal
      v-model="detailVisible"
      :title="detailTitle"
      :width="760"
      :mask-closable="false"
    >
      <div v-if="detailVisible" class="detail-content">
        <Spin v-if="detailLoading" fix />
        <template v-else>
          <div class="description-grid">
            <div class="description-item">
              <span>用户ID</span>
              <CopyableText :value="detailOrder.userId" strong />
            </div>
            <div class="description-item">
              <span>订单号</span>
              <CopyableText :value="detailOrder.orderNo" strong />
            </div>
            <div class="description-item">
              <span>第三方平台订单号</span>
              <CopyableText :value="detailPlatformOrderNo" code />
            </div>
            <div class="description-item">
              <span>平台交易ID</span>
              <CopyableText :value="operationTransactionId" code />
            </div>
            <div class="description-item">
              <span>订阅ID</span>
              <CopyableText :value="detailOrder.subscriptionId" code />
            </div>
            <div v-if="isRefundDetail" class="description-item">
              <span>平台退款ID</span>
              <CopyableText :value="detailOperation.platformRefundId" code />
            </div>
            <div class="description-item">
              <span>商品</span>
              <strong>{{ displayValue(detailOrder.goodsName) }}</strong>
            </div>
            <div class="description-item">
              <span>商品类型</span>
              <strong>{{ getGoodsTypeLabel(detailOrder.goodsType) }}</strong>
            </div>
            <div class="description-item">
              <span>支付方式</span>
              <strong>{{
                detailOrder.payPlatformName ||
                getPayPlatformLabel(detailOrder.payPlatform)
              }}</strong>
            </div>
            <div class="description-item">
              <span>支付金额</span>
              <strong>{{
                formatTableAmount(detailOrder.amount, detailOrder.currency)
              }}</strong>
            </div>
            <div class="description-item">
              <span>售后类型</span>
              <Tag
                :color="getOperationTypeColor(detailOperation.operationType)"
              >
                {{
                  detailOperation.operationTypeName ||
                  getOperationTypeLabel(detailOperation.operationType)
                }}
              </Tag>
            </div>
            <div class="description-item">
              <span>处理状态</span>
              <Tag
                :color="getOperationStatusColor(detailOperation.operationStatus)"
              >
                {{
                  detailOperation.operationStatusName ||
                  getOperationStatusLabel(detailOperation.operationStatus)
                }}
              </Tag>
            </div>
            <div class="description-item">
              <span>操作编号</span>
              <CopyableText :value="detailOperation.operationNo" code />
            </div>
            <div class="description-item">
              <span>{{ operationAmountLabel }}</span>
              <strong>{{
                formatTableAmount(
                  detailOperation.amount,
                  detailOperation.currency || detailOrder.currency
                )
              }}</strong>
            </div>
            <div class="description-item">
              <span>支付状态</span>
              <Tag :color="getPayStatusColor(detailOrder.payStatus)">
                {{ getPayStatusLabel(detailOrder.payStatus) }}
              </Tag>
            </div>
            <div v-if="isRefundDetail" class="description-item">
              <span>退款状态</span>
              <Tag :color="getRefundStatusColor(detailOrder.refundStatus)">
                {{ getRefundStatusLabel(detailOrder.refundStatus) }}
              </Tag>
            </div>
            <div class="description-item">
              <span>状态</span>
              <strong v-if="detailOrder.goodsType === 0" class="coin-change">
                <template
                  v-if="
                    detailOrder.payCoinGranted || detailOrder.bonusCoinGranted
                  "
                >
                  <span v-if="detailOrder.payCoinGranted">
                    +{{ detailOrder.payCoinGranted }}充值金币
                  </span>
                  <span v-if="detailOrder.bonusCoinGranted">
                    +{{ detailOrder.bonusCoinGranted }}赠送金币
                  </span>
                </template>
                <span v-else>—</span>
              </strong>
              <Tag
                v-else
                :color="
                  getSubscriptionStatusColor(detailOrder.subscriptionStatus)
                "
              >
                {{
                  detailOrder.subscriptionStatusName ||
                  getSubscriptionStatusLabel(detailOrder.subscriptionStatus)
                }}
              </Tag>
            </div>
            <div class="description-item">
              <span>处理时间</span>
              <strong>{{ formatDateTime(detailOperation.operateTime) }}</strong>
            </div>
            <div class="description-item">
              <span>操作人员</span>
              <strong>{{ detailOperatorName }}</strong>
            </div>
            <div v-if="detailOperation.finishTime" class="description-item">
              <span>完成时间</span>
              <strong>{{ formatDateTime(detailOperation.finishTime) }}</strong>
            </div>
            <template v-if="isRefundDetail">
              <div class="description-item">
                <span>扣除权益</span>
                <Tag :color="detailOperation.deductEquity ? 'warning' : 'default'">
                  {{ detailOperation.deductEquity ? '是' : '否' }}
                </Tag>
              </div>
              <div class="description-item">
                <span>权益处理状态</span>
                <Tag
                  :color="getEquityStatusColor(detailOperation.equityStatus)"
                >
                  {{ getEquityStatusLabel(detailOperation.equityStatus) }}
                </Tag>
              </div>
              <div class="description-item description-item-full">
                <span>已使用权益仍退款</span>
                <strong>{{
                  detailOperation.allowUsedEquity
                    ? '运营已确认允许'
                    : '否'
                }}</strong>
              </div>
            </template>
            <div
              v-if="detailOperation.reason"
              class="description-item description-item-full"
            >
              <span>操作原因</span>
              <strong>{{ detailOperation.reason }}</strong>
            </div>
            <div
              v-if="detailOperation.remark"
              class="description-item description-item-full"
            >
              <span>补充说明</span>
              <strong>{{ detailOperation.remark }}</strong>
            </div>
            <div
              v-if="detailOperation.failureMessage"
              class="description-item description-item-full"
            >
              <span>失败原因</span>
              <strong class="failure-message">
                {{ detailOperation.failureMessage }}
              </strong>
            </div>
          </div>

          <template v-if="hasEquitySnapshot">
            <div class="diff-title">退款权益变化</div>
            <Table
              border
              size="small"
              :columns="equityColumns"
              :data="equityRows"
              no-data-text="暂无权益变化"
            />
          </template>
          <Alert
            v-else-if="isRefundDetail && detailOperation.deductEquity"
            type="warning"
            show-icon
          >
            本次退款设置了扣除权益，但未记录到操作前后快照，请核对权益处理状态。
          </Alert>
        </template>
      </div>
      <div slot="footer">
        <Button type="primary" @click="detailVisible = false">关闭</Button>
      </div>
    </Modal>
  </Card>
</template>

<script>
import Pages from '@/components/pages'
import CopyableText from '../components/copyable-text.vue'
import {
  getAfterSalesDetail,
  getAfterSalesList,
  getAfterSalesOverview
} from '@/api/customer-service'
import {
  OPERATION_TYPE_OPTIONS,
  OVERVIEW_SCOPE_OPTIONS,
  compactObject,
  displayValue,
  formatDateTime,
  getEquityStatusColor,
  getEquityStatusLabel,
  getGoodsTypeLabel,
  getOperationTypeColor,
  getOperationTypeLabel,
  getOperationStatusColor,
  getOperationStatusLabel,
  getPayPlatformLabel,
  getPayStatusColor,
  getPayStatusLabel,
  getRefundStatusColor,
  getRefundStatusLabel,
  getSubscriptionStatusColor,
  getSubscriptionStatusLabel
} from '../constants'

export default {
  name: 'ComplaintCenterAfterSales',
  components: {
    CopyableText,
    Pages
  },
  data() {
    return {
      operationTypeOptions: OPERATION_TYPE_OPTIONS,
      overviewLoading: false,
      overviewData: [],
      searchForm: {
        keyword: '',
        operationType: null
      },
      tableLoading: false,
      tableData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      detailVisible: false,
      detailLoading: false,
      selectedOperationId: null,
      selectedAfterSaleRecord: {},
      detailData: {
        order: {},
        operations: []
      },
      columns: [
        { title: '用户ID', slot: 'userId', width: 170, resizable: true },
        { title: '订单ID', slot: 'orderId', width: 120, resizable: true },
        { title: '订单号', slot: 'orderNo', width: 170, resizable: true },
        {
          title: '第三方平台订单号',
          slot: 'platformOrderNo',
          width: 190,
          resizable: true
        },
        {
          title: '订阅ID',
          slot: 'subscriptionId',
          width: 170,
          resizable: true
        },
        { title: '商品', slot: 'goodsName', width: 140, resizable: true },
        { title: '商品类型', slot: 'goodsType', width: 100, resizable: true },
        { title: '支付方式', slot: 'payPlatform', width: 110, resizable: true },
        { title: '支付金额', slot: 'amount', width: 110, resizable: true },
        {
          title: '本次金额',
          slot: 'operationAmount',
          width: 110,
          resizable: true
        },
        {
          title: '售后类型',
          slot: 'operationType',
          width: 110,
          resizable: true
        },
        { title: '支付状态', slot: 'payStatus', width: 100, resizable: true },
        {
          title: '处理状态',
          slot: 'operationStatus',
          width: 100,
          resizable: true
        },
        { title: '订阅状态', slot: 'status', width: 130, resizable: true },
        { title: '处理时间', slot: 'operateTime', width: 170, resizable: true },
        { title: '操作人员', slot: 'operator', width: 120, resizable: true },
        {
          title: '操作',
          slot: 'action',
          fixed: 'right',
          width: 100,
          resizable: true
        }
      ],
      equityColumns: [
        { title: '字段', key: 'label', width: 160 },
        { title: '操作前', key: 'before', align: 'center' },
        { title: '操作后', key: 'after', align: 'center' }
      ]
    }
  },
  computed: {
    overviewCards() {
      return OVERVIEW_SCOPE_OPTIONS.map((scope) => {
        const data =
          this.overviewData.find((item) => item.scope === scope.value) || {}
        return {
          ...scope,
          totalCount: data.totalCount || 0,
          refundCount: data.refundCount || 0,
          refundAmount: data.refundAmount || 0,
          supplementCount: data.supplementCount || 0,
          cancelSubscriptionCount: data.cancelSubscriptionCount || 0
        }
      })
    },
    detailOrder() {
      return this.detailData.order || {}
    },
    detailOperation() {
      const operations = this.detailData.operations || []
      return (
        operations.find(
          (item) =>
            String(item.operationId) === String(this.selectedOperationId)
        ) ||
        operations[0] ||
        {}
      )
    },
    isRefundDetail() {
      return this.detailOperation.operationType === 1
    },
    detailTitle() {
      const typeName =
        this.detailOperation.operationTypeName ||
        getOperationTypeLabel(this.detailOperation.operationType)
      return typeName === '-' ? '售后详情' : `${typeName}详情`
    },
    operationAmountLabel() {
      return this.isRefundDetail ? '本次退款金额' : '本次操作金额'
    },
    operationTransactionId() {
      return this.detailOperation.transactionId || this.detailOrder.transactionId
    },
    detailPlatformOrderNo() {
      return (
        this.detailOrder.platformOrderNo ||
        this.selectedAfterSaleRecord.platformOrderNo
      )
    },
    detailOperatorName() {
      return (
        this.selectedAfterSaleRecord.operatorName ||
        this.detailOperation.operatorName ||
        '管理员'
      )
    },
    hasEquitySnapshot() {
      return !!(
        this.detailOperation.equityBefore || this.detailOperation.equityAfter
      )
    },
    equityRows() {
      const before = this.detailOperation.equityBefore || {}
      const after = this.detailOperation.equityAfter || {}
      return [
        {
          label: '快照时间',
          before: formatDateTime(before.snapshotTime),
          after: formatDateTime(after.snapshotTime)
        },
        {
          label: '用户是否为VIP',
          before: this.formatBoolean(before.vipStatus),
          after: this.formatBoolean(after.vipStatus)
        },
        {
          label: 'VIP有效期',
          before: formatDateTime(before.vipExpireTime),
          after: formatDateTime(after.vipExpireTime)
        },
        {
          label: '用户是否有通行证',
          before: this.formatBoolean(before.passStatus),
          after: this.formatBoolean(after.passStatus)
        },
        {
          label: '通行证有效期',
          before: formatDateTime(before.passExpireTime),
          after: formatDateTime(after.passExpireTime)
        },
        {
          label: '用户金币数量',
          before: this.displaySnapshotValue(before.balanceNum),
          after: this.displaySnapshotValue(after.balanceNum)
        },
        {
          label: '充值金币余额',
          before: this.displaySnapshotValue(before.payBalance),
          after: this.displaySnapshotValue(after.payBalance)
        },
        {
          label: '赠币余额',
          before: this.displaySnapshotValue(before.bonusBalance),
          after: this.displaySnapshotValue(after.bonusBalance)
        }
      ]
    }
  },
  mounted() {
    this.loadOverview()
    this.getData()
  },
  methods: {
    loadOverview() {
      this.overviewLoading = true
      getAfterSalesOverview()
        .then(({ data }) => {
          this.overviewData = data || []
        })
        .finally(() => {
          this.overviewLoading = false
        })
    },
    getData(page) {
      if (page) this.page.current = page
      else this.page.current = 1
      this.tableLoading = true
      getAfterSalesList(
        compactObject({
          current: this.page.current,
          size: this.page.size,
          ...this.searchForm
        })
      )
        .then(({ data }) => {
          const result = data || {}
          this.tableData = result.records || []
          this.page.current = Number(result.current) || 1
          this.page.size = Number(result.size) || this.page.size
          this.page.total = Number(result.total) || 0
        })
        .finally(() => {
          this.tableLoading = false
        })
    },
    handleReset() {
      this.searchForm = {
        keyword: '',
        operationType: null
      }
      this.getData()
    },
    handleOpenDetail(row) {
      this.selectedOperationId = row.operationId
      this.selectedAfterSaleRecord = row
      this.detailVisible = true
      this.detailLoading = true
      this.detailData = {
        order: {},
        operations: []
      }
      getAfterSalesDetail(row.orderId)
        .then(({ data }) => {
          this.detailData = data || {
            order: {},
            operations: []
          }
        })
        .finally(() => {
          this.detailLoading = false
        })
    },
    formatOverviewAmount(amount) {
      const value = Number(amount)
      return `$${Number.isNaN(value) ? '0.00' : value.toFixed(2)}`
    },
    formatTableAmount(amount, currency) {
      if (amount === null || amount === undefined || amount === '') return '-'
      const value = Number(amount)
      if (Number.isNaN(value)) return '-'
      const code = currency === 'USD' ? '$' : currency || '$'
      return `${code} ${value.toFixed(2)}`
    },
    formatBoolean(value) {
      if (value === true) return '是'
      if (value === false) return '否'
      return '-'
    },
    displaySnapshotValue(value) {
      return value === null || value === undefined ? '-' : value
    },
    displayValue,
    formatDateTime,
    getGoodsTypeLabel,
    getEquityStatusColor,
    getEquityStatusLabel,
    getOperationTypeColor,
    getOperationTypeLabel,
    getOperationStatusColor,
    getOperationStatusLabel,
    getPayPlatformLabel,
    getPayStatusColor,
    getPayStatusLabel,
    getRefundStatusColor,
    getRefundStatusLabel,
    getSubscriptionStatusColor,
    getSubscriptionStatusLabel
  }
}
</script>

<style scoped lang="less">
.layout-card {
  height: 100%;

  /deep/ .ivu-card-body {
    height: 100%;
    padding: 0;
  }
}

.page-scroll {
  height: 100%;
  padding: 24px;
  overflow: auto;
}

.section-title {
  margin-bottom: 16px;
  color: #141414;
  font-size: 20px;
  line-height: 28px;
}

.overview-title {
  margin-bottom: 12px;
}

.overview-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(240px, 1fr));
  gap: 16px;
}

.overview-card {
  position: relative;
  overflow: hidden;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fff;
}

.overview-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 38px;
  padding: 0 12px;
  border-bottom: 1px solid #f0f0f0;
}

.overview-card-body {
  padding: 10px 12px 12px;
}

.refund-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 62px;
  margin-bottom: 8px;
  padding: 8px 12px;
  border-radius: 4px;
  background: #fafafa;
}

.summary-name {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #595959;
  font-size: 13px;
}

.summary-values {
  display: flex;
  align-items: center;
  gap: 14px;

  span {
    display: flex;
    flex-direction: column;
    min-width: 38px;
    text-align: center;
  }

  em {
    color: #8c8c8c;
    font-size: 12px;
    font-style: normal;
  }

  strong {
    color: #ff4d4f;
    font-size: 16px;
    line-height: 22px;
  }
}

.operation-summary-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.operation-summary {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 88px;
  border-radius: 4px;
  background: #fafafa;
  color: #595959;
  font-size: 12px;

  span {
    margin: 4px 0 2px;
  }

  strong {
    color: #1677ff;
    font-size: 18px;
  }

  .cancel-count {
    color: #f5222d;
  }
}

.section-divider {
  margin: 16px 0;
}

.search-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 58px;
  margin-bottom: 16px;
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fff;
}

.keyword-input {
  width: 260px;
}

.type-select {
  width: 140px;
}

.table-wrap {
  min-height: 220px;

  /deep/ .ivu-table {
    color: #141414;
  }

  /deep/ .ivu-table-header,
  /deep/ .ivu-table-fixed-header {
    height: 55px;
  }

  /deep/ .ivu-table-header th,
  /deep/ .ivu-table-fixed-header th {
    height: 55px;
    background: #fafafa;
    color: #141414;
    font-weight: 600;
  }

  /deep/ .ivu-table-cell {
    padding: 0 16px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /deep/ .ivu-table-body td,
  /deep/ .ivu-table-fixed-body td,
  /deep/ .ivu-table-body tr,
  /deep/ .ivu-table-fixed-body tr {
    height: 58px;
  }

  /deep/ .ivu-table-tip td {
    height: 160px;
  }
}

.mono {
  font-family: Consolas, Monaco, monospace;
  font-size: 13px;
}

.empty-status {
  color: #999;
}

.coin-change {
  color: #fa541c;
  font-size: 12px;
  font-weight: 400;

  span {
    display: block;
  }
}

.detail-content {
  position: relative;
  min-height: 160px;
}

.description-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  overflow: hidden;
  margin-bottom: 16px;
  border-top: 1px solid #e8eaec;
  border-left: 1px solid #e8eaec;
}

.description-item {
  display: grid;
  grid-template-columns: 112px 1fr;
  min-height: 40px;
  border-right: 1px solid #e8eaec;
  border-bottom: 1px solid #e8eaec;

  > span:first-child {
    display: flex;
    align-items: center;
    padding: 8px 10px;
    background: #fafafa;
    color: #515a6e;
  }

  > strong,
  > .ivu-tag,
  > .copyable-text {
    align-self: center;
    margin-left: 10px;
    font-weight: 400;
  }
}

.description-item-full {
  grid-column: 1 / -1;
}

.dual-user-id {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;

  > small {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #9aa1ac;
    font-size: 11px;
  }
}

.list-pagination {
  margin-top: 16px;
}

.failure-message {
  color: #ed4014;
}

.diff-title {
  margin-bottom: 8px;
  font-weight: 600;
}

@media (max-width: 991px) {
  .overview-grid {
    grid-template-columns: repeat(2, minmax(240px, 1fr));
  }
}
</style>
